import { useEffect, useRef, useState } from 'react';
import { FaList, FaPrint, FaSearch, FaPalette, FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from "react-to-print";
import CommonButton from '../components/CommonButton';
import CustomFormGroup from '../components/CustomFormGroup';
import MemberCard from '../components/MemberCard';
import ModalConfirm from '../components/ModalConfirm';
import Pagination from '../components/Pagination';
import SkillModal from "../components/SkillModal";
import MessageModal from '../components/MessageModal';
import { getMembers, blockirMember, unblockirMember, getMembersBySkills } from '../services/api/memberService';
import '../styles/MemberList.css';
import { useLoading } from "../components/LoadingContext";

const MemberListPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [blockirError, setBlockirError] = useState('');
  const [unblockirError, setUnblockirError] = useState('');
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [memberToPrint, setMemberToPrint] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]); // multiple
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);
  const [isUnblockModalOpen, setIsUnblockModalOpen] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const { showLoading, hideLoading } = useLoading();

  const limitOptions = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: 'All', value: 'All' },
  ];

  const singleCardRef = useRef();
  const allCardsRef = useRef();

  // === FETCH MEMBERS ===
  const fetchMembers = async () => {
    setLoading(true);

    let data = [];
    let count = 0;

    if (selectedSkills.length > 0) {
      const skillIds = selectedSkills.map(s => s.id);
      const result = await getMembersBySkills(skillIds, search, limit, (page - 1) * limit);
      data = result.data;
      count = result.count;
    } else {
      const result = await getMembers(search, limit, (page - 1) * limit);
      data = result.data;
      count = result.count;
    }

    setMembers(data || []);
    setTotalCount(count || 0);
    setLoading(false);
  };

  const fetchAllMembers = async () => {
    const { data } = await getMembers(search, "All", 0);
    data.filter(member => member.is_active === 1);
    setAllMembers(data || []);
  };

  useEffect(() => {
    fetchMembers();
  }, [page, search, limit, selectedSkills]);

  const handleLimitChange = (e) => {
    const value = e.target.value;
    setLimit(value === "All" ? "All" : parseInt(value));
    setPage(1);
  };

  const handleEdit = (member) => navigate(`/edit-member/${member.id}`, {
    state: { from: '/members' }
  });

  const handlePrintAll = useReactToPrint({
    contentRef: allCardsRef,
    documentTitle: "All"
  });

  const printSingle = useReactToPrint({
    contentRef: singleCardRef,
    onAfterPrint: () => setMemberToPrint(null)
  });

  const printAllClick = async () => {
    await fetchAllMembers();
    setTimeout(() => handlePrintAll(), 300);
  };

  useEffect(() => {
    if (memberToPrint) {
      setTimeout(() => {
        document.title = memberToPrint.member_id;
        printSingle();
      }, 300);
    }
  }, [memberToPrint]);

  const handleBlock = (member) => {
    setSelectedMember(member);
    setIsModalConfirmOpen(true);
  };

  const handleSubmitBlockir = async (reason) => {
    if (isBlocking) return;

    if (!reason.trim()) {
      alert('Please fill blockir reason');
      return;
    }

    showLoading();
    setIsBlocking(true);             
    setBlockirError('');

    try {
      const result = await blockirMember(selectedMember.id, reason);

      if (!result.success) {
        setBlockirError(result.message);
      } else {
        await fetchMembers();
      }

      setIsResponseModalOpen(true);
    } finally {
      setIsBlocking(false);
      hideLoading();
    }
  };


  const handleUnblock = async (member) => {
    if (isUnblocking) return;        

    showLoading();
    setIsUnblocking(true);
    setUnblockirError('');
    setSelectedMember(member);

    try {
      const result = await unblockirMember(member.id);

      if (!result.success) {
        setUnblockirError(result.message);
      } else {
        await fetchMembers();
      }

      setIsUnblockModalOpen(true);
    } finally {
      setIsUnblocking(false);
      hideLoading();
    }
  };


  const handleCloseUnblockModal = async () => {
    setIsUnblockModalOpen(false);
    await fetchMembers();
  }

  const totalPages = limit === "All" ? 1 : Math.ceil(totalCount / limit);

  return (
    <div className="member-page-container">
      <div className="member-info-dashboard">
        <div className="member-info-header">
          <FaInfoCircle className="info-icon" />
          <strong>Member Code Information</strong>
        </div>

        <p className="member-info-desc">
          KSN + Member Type + Registration Date (YYYYMMDD) + 5 Digit Running Number
        </p>

        <div className="member-info-list">
          <strong>Member Type:</strong>
          <ul className="member-type-list">
            <li>
              <span className="member-type-code">A</span>
              <span className="member-type-desc">
                Yogi ODM Bhante Kondanna (KSN)
              </span>
            </li>
            <li>
              <span className="member-type-code">A</span>
              <span className="member-type-desc">
                Yogi Retreat Bhante Kondanna (KSN)
              </span>
            </li>
            <li>
              <span className="member-type-code">B</span>
              <span className="member-type-desc">
                Volunteer KSN
              </span>
            </li>
            <li>
              <span className="member-type-code">C</span>
              <span className="member-type-desc">
                Yogi ODM / Retreat Bhante Kondanna (Outside KSN)
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div className="member-page-header">
        <h2>Member List</h2>
        <div className="search-members-wrapper">
          {/* Limit */}
          <CustomFormGroup
            type="select"
            options={limitOptions}
            value={limit}
            onChange={handleLimitChange}
            icon={<FaList />}
          />

          {/* Search */}
          <CustomFormGroup
            type="text"
            placeholder="Search by name or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<FaSearch />}
          />

          {/* Filter by skills */}
          <CustomFormGroup
            type="chipSelect"
            name="skills"
            value={selectedSkills}
            placeholder="Filter by skills"
            onChange={(e) => setSelectedSkills(e.target.value)}
            onOpenPopup={() => setIsSkillModalOpen(true)}
            readOnly
            icon={<FaPalette />}
          />
          {isSkillModalOpen && (
            <SkillModal
              selectedSkills={selectedSkills}
              onSave={(skills) => {
                setSelectedSkills(skills);
                setIsSkillModalOpen(false);
              }}
              onClose={() => setIsSkillModalOpen(false)}
            />
          )}
        </div>
        <div className='print-all-button'>
          <CommonButton onClick={printAllClick}><FaPrint /> Print All</CommonButton>
        </div>
      </div>

      {/* === LIST MEMBER === */}
      <div className="member-list-container">
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : (
          members.length > 0 ? (
            members.map((member) => (
              <div key={member.member_id}>
                <MemberCard
                  member={member}
                  onEdit={handleEdit}
                  onBlock={handleBlock}
                  onUnblock={handleUnblock}
                  onPrint={() => setMemberToPrint(member)}
                  isActive={member.is_active}
                  showActions={1}
                  isUnblocking={isUnblocking}
                />
              </div>
            ))
          ) : (
            <p className="no-result">No members found.</p>
          )
        )}
      </div>

      {/* === Hidden print refs === */}
      <div style={{ display: "none" }}>
        <div ref={singleCardRef}>
          {memberToPrint && <MemberCard member={memberToPrint} onEdit={handleEdit} onBlock={handleBlock} />}
        </div>
      </div>

      <div style={{ display: "none" }}>
        <div ref={allCardsRef}>
          {allMembers.map((member) => (
            <div key={member.member_id}>
              <MemberCard member={member} onEdit={handleEdit} onBlock={handleBlock} />
            </div>
          ))}
        </div>
      </div>

      {/* === Pagination === */}
      {totalCount > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* === Blockir Modal === */}
      {isModalConfirmOpen && selectedMember && (
        <ModalConfirm
          member={selectedMember}
          onClose={() => setIsModalConfirmOpen(false)}
          title="Blockir Confirmation"
          message={`Are you sure want to block ${selectedMember.full_name}?`}
          showTextBox={true}
          onClickYes={handleSubmitBlockir}
          onClickNo={() => setIsModalConfirmOpen(false)}
          error={blockirError}
          isModalOpen={isModalConfirmOpen}
          responseTitle={blockirError === '' ? 'Success' : 'Failed'}
          responseMessage={
            blockirError === ''
              ? `Successfully blocked ${selectedMember.full_name}`
              : `Failed to block ${selectedMember.full_name} due to: ${blockirError}`
          }
          isResponseModal={isResponseModalOpen}
          onResponseClose={() => {
            setIsResponseModalOpen(false);
            setIsModalConfirmOpen(false);
          }}
          isLoading={isBlocking}
        />
      )}


      {isUnblockModalOpen && (
        <MessageModal 
          onClose={handleCloseUnblockModal}
          title={unblockirError == '' ? 'Successfully Unblocked' : 'Unblock Failed'}
          message_content={unblockirError == '' ? 'Your data changes were successfully saved.' : unblockirError}  
          icon={unblockirError == '' ? <FaCheckCircle/> : <FaTimesCircle/>}
          button_name={'Close'} 
          isSuccess={unblockirError == '' ? true : false} 
        />
      )}
    </div>
  );
};

export default MemberListPage;
